// @ts-nocheck
/*
 * Port of views/dashboard/release-center/list.js (322 lines). BYTE-IDENTICAL.
 *
 * SUPERADMIN-only Release Center page. Hero + toolbar (search/compare/create)
 * + filter chips + collapsible InstructionCard + timeline (with optional
 * pinned hero) + detail/form/compare modals + floating dock.
 *
 * V1 quirks preserved:
 *  - Share button is commented out in the toolbar (kept as JSX comment);
 *    only the FloatingDock's `onSearch` invocation actually exposes share.
 *    Actually — v1 has NO share in the dock either; kept verbatim.
 *  - `linkHandled` guard prevents re-opening the deep-link detail modal
 *    once the initial `?open=` has been consumed.
 *  - `copyLink` fallback to `window.prompt` when clipboard is blocked.
 *  - `focusSearch` calls both `focus()` and `scrollIntoView`.
 *  - `scrollTop` targets the `.content-page, .main-content` container as
 *    well as window — matches v1's iron-panel scroll surface hunt.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';

import { getUser } from '@/utilities/setting';
import { ROUTES, USER_ROLES } from '@/utilities/constant';

import useReleaseCenter from './useReleaseCenter';
import {
  setReleasePublished,
  setReleasePinned,
  setReleaseDisabled,
  updateRelease,
} from './releaseCenterService';
import { applyReleaseFilters, FILTER_CHIPS } from './releaseUtils';

import ReleaseHero from './ReleaseHero';
import FilterChips from './FilterChips';
import PinnedHero from './PinnedHero';
import ReleaseTimeline from './ReleaseTimeline';
import EmptyState from './EmptyState';
import ReleaseSkeleton from './ReleaseSkeleton';
import ReleaseDetailModal from './ReleaseDetailModal';
import ReleaseFormModal from './ReleaseFormModal';
import ReleaseCompareModal from './ReleaseCompareModal';
import ReleaseFloatingDock from './ReleaseFloatingDock';
import InstructionCard from './InstructionCard';

import './release-center.css';
import { FillPage } from '@/components/layout/PageContainer';

const ReleaseCenter = () => {
  const user = getUser();
  const isSuperAdmin = user?.role === USER_ROLES.SUPERADMIN;

  const { releases, stats, tags, loading, refetch } = useReleaseCenter();

  const [searchParams] = useSearchParams();
  const [search, setSearch] = useState(searchParams.get('search') || '');
  const [activeChip, setActiveChip] = useState<any>(() => {
    const f = searchParams.get('filter');
    return f
      ? FILTER_CHIPS.find((c: any) => c.key === f && c.kind !== 'all') || null
      : null;
  });
  const [linkHandled, setLinkHandled] = useState(false);
  const [detail, setDetail] = useState<any>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<any>(null);
  const [compareOpen, setCompareOpen] = useState(false);
  const [busyId, setBusyId] = useState<any>(null);

  const searchRef = useRef<any>(null);

  const isFiltering = Boolean(search.trim() || activeChip);

  const filtered = useMemo(
    () => applyReleaseFilters(releases, search, activeChip),
    [releases, search, activeChip],
  );

  // Pinned marquee only when browsing the full, unfiltered timeline.
  const pinned = useMemo(
    () => releases.find((r: any) => r.is_pinned) || null,
    [releases],
  );
  const timelineReleases = useMemo(() => {
    if (isFiltering) return filtered;
    return pinned ? filtered.filter((r: any) => r.id !== pinned.id) : filtered;
  }, [filtered, pinned, isFiltering]);

  useEffect(() => {
    if (linkHandled || !releases.length) return;
    const openId = searchParams.get('open');
    if (openId) {
      const target = releases.find(
        (r: any) => String(r.id) === String(openId),
      );
      if (target) setDetail(target);
    }
    setLinkHandled(true);
  }, [releases, searchParams, linkHandled]);

  const buildShareUrl = ({ q, chip, openId }: any = {}) => {
    const base = `${window.location.origin}/${ROUTES.RELEASE_CENTER.LIST}`;
    const p = new URLSearchParams();
    if (q && q.trim()) p.set('search', q.trim());
    if (chip) p.set('filter', chip.key);
    if (openId) p.set('open', openId);
    const qs = p.toString();
    return qs ? `${base}?${qs}` : base;
  };

  const copyLink = async (url: string) => {
    const msg = 'Link copied — it opens only for Super Admins.';
    try {
      await navigator.clipboard.writeText(url);
      toast.success(msg);
    } catch {
      window.prompt('Copy this Super-Admin link:', url);
    }
  };

  const shareCurrentView = () =>
    copyLink(buildShareUrl({ q: search, chip: activeChip }));
  const shareRelease = (r: any) => copyLink(buildShareUrl({ openId: r.id }));

  // ---- actions ----
  const openDetail = (r: any) => setDetail(r);
  const openCreate = () => {
    setEditing(null);
    setFormOpen(true);
  };
  const openEdit = (r: any) => {
    setEditing(r);
    setFormOpen(true);
  };

  const doTogglePublish = async (r: any) => {
    setBusyId(r.id);
    try {
      await setReleasePublished(r.id, !r.is_published);
      toast.success(r.is_published ? 'Moved to draft' : 'Published');
      await refetch({ silent: true });
    } catch {
      /* toasted */
    } finally {
      setBusyId(null);
    }
  };

  const doTogglePin = async (r: any) => {
    setBusyId(r.id);
    try {
      await setReleasePinned(r.id, !r.is_pinned);
      toast.success(r.is_pinned ? 'Unpinned' : 'Pinned');
      await refetch({ silent: true });
    } catch {
      /* toasted */
    } finally {
      setBusyId(null);
    }
  };

  const doToggleDisable = async (r: any) => {
    setBusyId(r.id);
    try {
      const updated: any = await setReleaseDisabled(r.id, !r.is_disabled);
      toast.success(r.is_disabled ? 'Release enabled' : 'Release disabled');
      if (detail && detail.id === r.id && updated) setDetail(updated);
      await refetch({ silent: true });
    } catch {
      /* toasted */
    } finally {
      setBusyId(null);
    }
  };

  const editFromDetail = (r: any) => {
    setDetail(null);
    openEdit(r);
  };

  // Import a PDF/Word file directly into an open release and persist it.
  const importIntoRelease = async (r: any, html: any) => {
    const updated: any = await updateRelease(r.id, { content_html: html });
    if (updated) setDetail(updated);
    await refetch({ silent: true });
  };

  const focusSearch = () => {
    searchRef.current?.focus();
    searchRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const scrollTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    (
      document.querySelector('.content-page, .main-content') as any
    )?.scrollTo?.({ top: 0, behavior: 'smooth' });
  };

  const resetFilters = () => {
    setSearch('');
    setActiveChip(null);
  };

  const cardProps = {
    canManage: isSuperAdmin,
    onOpen: openDetail,
    onEdit: openEdit,
    onTogglePublish: doTogglePublish,
    onTogglePin: doTogglePin,
    onToggleDisable: doToggleDisable,
    onShare: shareRelease,
    busy: false,
  };

  if (!isSuperAdmin) {
    return (
      <FillPage>
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          You do not have access to this page.
        </div>
      </FillPage>
    );
  }

  return (
    <FillPage>
      <div className="release-center">
      <ReleaseHero stats={stats} />

      {/* Toolbar: instant search + compare + create */}
      <div className="rc-toolbar">
        <div className="rc-search">
          <span className="rc-search__icon" aria-hidden="true">
            🔍
          </span>
          <input
            ref={searchRef}
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search version, title, summary, tags, content…"
            aria-label="Search releases"
          />
        </div>
        {/* <button
          type="button"
          className="rc-btn"
          onClick={shareCurrentView}
          title="Copy a shareable link to this view (Super Admins only)"
        >
          🔗 Share
        </button> */}
        <button
          type="button"
          className="rc-btn"
          onClick={() => setCompareOpen(true)}
          disabled={releases.length < 2}
        >
          ⇄ Compare
        </button>
        <button
          type="button"
          className="rc-btn rc-btn--primary"
          onClick={openCreate}
        >
          + New release
        </button>
      </div>

      <FilterChips active={activeChip} onChange={setActiveChip} />

      {/* Developer guide — dismissible, super-admin only (page is already gated) */}
      <InstructionCard />

      {/* Body */}
      {loading ? (
        <div style={{ marginTop: 28 }}>
          <ReleaseSkeleton count={3} />
        </div>
      ) : releases.length === 0 ? (
        <div style={{ marginTop: 28 }}>
          <EmptyState
            variant="no-releases"
            canManage={isSuperAdmin}
            onCreate={openCreate}
          />
        </div>
      ) : (
        <>
          {!isFiltering && pinned && (
            <>
              <div className="rc-section-title">Highlighted</div>
              <PinnedHero release={pinned} onOpen={openDetail} />
            </>
          )}

          <div className="rc-section-title">
            {isFiltering ? 'Results' : 'Timeline'}
          </div>

          {timelineReleases.length === 0 ? (
            <EmptyState variant="no-results" onReset={resetFilters} />
          ) : (
            <ReleaseTimeline
              releases={timelineReleases}
              cardProps={{ ...cardProps, busy: busyId }}
            />
          )}
        </>
      )}

      {/* Modals */}
      <ReleaseDetailModal
        show={!!detail}
        release={detail}
        onHide={() => setDetail(null)}
        onShare={shareRelease}
        canManage={isSuperAdmin}
        onImportContent={importIntoRelease}
        onEdit={editFromDetail}
        onToggleDisable={doToggleDisable}
      />
      <ReleaseFormModal
        show={formOpen}
        release={editing}
        tags={tags}
        onHide={() => setFormOpen(false)}
        onSaved={() => refetch({ silent: true })}
      />
      <ReleaseCompareModal
        show={compareOpen}
        onHide={() => setCompareOpen(false)}
        releases={releases}
      />

      {/* Apple-style floating helper dock */}
      <ReleaseFloatingDock
        canManage={isSuperAdmin}
        onSearch={focusSearch}
        onCreate={openCreate}
        onCompare={() => setCompareOpen(true)}
        onScrollTop={scrollTop}
      />
      </div>
    </FillPage>
  );
};

export default ReleaseCenter;
